import { Eye, EyeOff, KeyRound, Save, UserRound } from "lucide-react";
import { useState } from "react";
import { Layout } from "../components/Layout";
import { useAuth } from "../contexts";
import { api } from "../lib/api";
import { languages } from "../lib/constants";
import type { User } from "@shared/types";

export default function SettingsPage() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", age: user?.age ?? 18, targetLanguage: user?.targetLanguage || "en", level: user?.level || "beginner" });
  const [passwords, setPasswords] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [showPasswords, setShowPasswords] = useState(false);
  const [message, setMessage] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage("");
    setError("");
    try {
      const result = await api<{ user: User }>("/api/settings", { method: "PUT", body: JSON.stringify(form) });
      setUser(result.user);
      setMessage("Preferências atualizadas com sucesso.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Erro ao salvar.");
    }
  };

  const changePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordMessage("");
    setPasswordError("");
    if (passwords.newPassword !== passwords.confirmPassword) {
      setPasswordError("A confirmação da nova senha não confere.");
      return;
    }
    try {
      await api("/api/auth/password", { method: "PUT", body: JSON.stringify({ currentPassword: passwords.currentPassword, newPassword: passwords.newPassword }) });
      setPasswords({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setPasswordMessage("Senha alterada com sucesso.");
    } catch (cause) {
      setPasswordError(cause instanceof Error ? cause.message : "Erro ao alterar senha.");
    }
  };

  return <Layout title="Preferências" subtitle="Ajuste seu perfil principal de aprendizagem e sua segurança.">
    <div className="settings-layout">
      <section className="panel profile-panel"><div className="profile-avatar"><UserRound/></div><h2>{user?.name}</h2><p>{user?.email}</p><span className="badge">{user?.role === "admin" ? "Administrador" : "Estudante"}</span></section>
      <div className="settings-stack">
        <form className="panel settings-form" onSubmit={save}>
          <div className="panel-title"><div><h3>Perfil de aprendizagem</h3><p>Essas opções serão usadas como padrão nas novas conversas.</p></div></div>
          {message && <div className="form-success">{message}</div>}{error && <div className="form-error">{error}</div>}
          <div className="form-grid two roomy">
            <label>Nome<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })}/></label>
            <label>Idade<input type="number" min="6" max="120" value={form.age} onChange={(event) => setForm({ ...form, age: Number(event.target.value) })}/></label>
            <label>Idioma principal<select value={form.targetLanguage} onChange={(event) => setForm({ ...form, targetLanguage: event.target.value as User["targetLanguage"] })}>{Object.entries(languages).map(([code, item]) => <option value={code} key={code}>{item.flag} {item.name}</option>)}</select></label>
            <label>Nível atual<select value={form.level} onChange={(event) => setForm({ ...form, level: event.target.value as User["level"] })}><option value="beginner">Iniciante</option><option value="basic">Básico</option><option value="intermediate">Intermediário</option><option value="advanced">Avançado</option></select></label>
          </div>
          <button className="primary-button"><Save/> Salvar alterações</button>
        </form>

        <form className="panel settings-form" onSubmit={changePassword}>
          <div className="panel-title"><div><h3>Alterar senha</h3><p>Use pelo menos seis caracteres e guarde a nova senha em local seguro.</p></div><KeyRound/></div>
          {passwordMessage && <div className="form-success">{passwordMessage}</div>}{passwordError && <div className="form-error">{passwordError}</div>}
          <div className="form-grid two roomy">
            <label>Senha atual<div className="password-field"><input type={showPasswords ? "text" : "password"} value={passwords.currentPassword} onChange={(event) => setPasswords({ ...passwords, currentPassword: event.target.value })}/><button type="button" onClick={() => setShowPasswords((value) => !value)} aria-label="Mostrar ou ocultar senhas">{showPasswords ? <EyeOff/> : <Eye/>}</button></div></label>
            <label>Nova senha<input type={showPasswords ? "text" : "password"} minLength={6} value={passwords.newPassword} onChange={(event) => setPasswords({ ...passwords, newPassword: event.target.value })}/></label>
            <label>Confirmar nova senha<input type={showPasswords ? "text" : "password"} minLength={6} value={passwords.confirmPassword} onChange={(event) => setPasswords({ ...passwords, confirmPassword: event.target.value })}/></label>
          </div>
          <button className="primary-button" disabled={!passwords.currentPassword || passwords.newPassword.length < 6 || !passwords.confirmPassword}><KeyRound/> Alterar senha</button>
        </form>
      </div>
    </div>
  </Layout>;
}
